import React from 'react';
import ErrorState from '../ui/ErrorState';

interface ViewErrorBoundaryProps {
  /** Remounts the boundary when the view changes, clearing a stale error. */
  resetKey: string;
  children: React.ReactNode;
}

interface ViewErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches a render-time crash in one view and keeps the rest of the console
 * usable.
 *
 * Without this, any unguarded property access in a panel — and there are
 * several, reading fields the API does not always return — unmounts the whole
 * React tree and leaves a white page with no navigation, no breadcrumb, and no
 * way back except editing the URL. A failed panel should cost the reader that
 * panel, not the console.
 */
class ViewErrorBoundary extends React.Component<ViewErrorBoundaryProps, ViewErrorBoundaryState> {
  state: ViewErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ViewErrorBoundaryState {
    return { error };
  }

  componentDidUpdate(previousProps: ViewErrorBoundaryProps) {
    // Navigating away is itself a recovery: the next view gets a clean slate.
    if (previousProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('View crashed:', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <ErrorState
          title="This view stopped working"
          detail={this.state.error.message}
          onRetry={() => this.setState({ error: null })}
        />
      );
    }
    return this.props.children;
  }
}

export default ViewErrorBoundary;
